import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button, Typography, Spin, Empty, message, Badge, Modal
} from 'antd';
import {
  ShoppingCartOutlined, ArrowLeftOutlined, StarFilled,
  ShareAltOutlined, LeftOutlined, RightOutlined,
  ShopOutlined, BgColorsOutlined
} from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import { formatImageUrl, fetchProductById, BASE_HOST } from '../../services/api';
import { Helmet } from 'react-helmet';
import SocialMetaTags from '../../components/SocialMetaTags';
import './ProductDetailsPage.css';
import axios from 'axios';

const { Title, Text } = Typography;

const ProductDetailsPage = ({ products = [], addToCart }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isImageModalVisible, setIsImageModalVisible] = useState(false);
  const [isReducedMotion, setIsReducedMotion] = useState(false);
  const galleryRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const [addedToCart, setAddedToCart] = useState(false);
  const [showMobileControls, setShowMobileControls] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [cardHeight, setCardHeight] = useState(50); // Initial card height percentage
  const [dragging, setDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [confetti, setConfetti] = useState([]);
  const detailCardRef = useRef(null);
  const dragStartRef = useRef(0);
  const lastCardHeightRef = useRef(50);
  const pageTopRef = useRef(null);

  // Detectar preferencias de reducción de movimiento
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setIsReducedMotion(mediaQuery.matches);

    const handleChange = (e) => setIsReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Scroll al inicio de la página cuando se carga
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]); // Se ejecutará cada vez que cambie el ID del producto

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const loadProductDetails = async () => {
      try {
        setLoading(true);
        const data = await fetchProductById(id);
        if (data) {
          // Obtener las imágenes del producto desde la API específica
          const imagenesResponse = await axios.get(`${BASE_HOST}/api/producto/${id}/imagenes`);
          const imagenes = imagenesResponse.data;

          // Actualizar el producto con sus imágenes
          setProduct({
            ...data,
            imagenes: imagenes
          });

          // Si hay imágenes, seleccionar la primera
          if (imagenes && imagenes.length > 0) {
            setSelectedImageIndex(0);
          }
        }
      } catch (error) {
        console.error('Error loading product:', error);
        message.error('Error al cargar el producto');
      } finally {
        setLoading(false);
      }
    };

    loadProductDetails();
  }, [id]);

  const getDiscountedPrice = () => {
    if (!product?.promotions || product.promotions.length === 0) {
      return null;
    }

    const promotion = product.promotions[0];
    if (promotion.type === 'descuento') {
      return product.price * (1 - promotion.value / 100);
    }
    return null;
  };

  const discountedPrice = product ? getDiscountedPrice() : null;
  const finalPrice = discountedPrice || (product?.price || 0);

  const handleIncreaseQuantity = () => setQuantity(prev => prev + 1);
  const handleDecreaseQuantity = () => setQuantity(prev => prev > 1 ? prev - 1 : 1);

  // Función para manejar el cambio de imagen
  const handleImageChange = (index) => {
    setSelectedImageIndex(index);
  };

  // Funciones para el swipe en móvil mejoradas
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = () => {
    const swipeDistanceX = touchEndX.current - touchStartX.current;
    const swipeDistanceY = touchEndY.current - touchStartY.current;
    const minSwipeDistance = 50;

    // Solo procesar swipe horizontal si es más horizontal que vertical
    if (Math.abs(swipeDistanceX) > Math.abs(swipeDistanceY) && Math.abs(swipeDistanceX) > minSwipeDistance) {
      if (swipeDistanceX > 0 && selectedImageIndex > 0) {
        // Swipe derecha - imagen anterior
        setSelectedImageIndex(prev => prev - 1);
      } else if (swipeDistanceX < 0 && selectedImageIndex < (product?.imagenes?.length || 1) - 1) {
        // Swipe izquierda - imagen siguiente
        setSelectedImageIndex(prev => prev + 1);
      }
    }
  };

  // Agregar referencia para touchStartY
  const touchStartY = useRef(0);
  const touchEndY = useRef(0);

  // Funciones para el modal de imagen
  const handlePrevImage = () => {
    if (selectedImageIndex > 0) {
      setSelectedImageIndex(prev => prev - 1);
    }
  };

  const handleNextImage = () => {
    if (selectedImageIndex < (product?.imagenes?.length || 1) - 1) {
      setSelectedImageIndex(prev => prev + 1);
    }
  };

  // Manejar teclas para navegación
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isImageModalVisible) return;

      if (e.key === 'ArrowLeft') {
        handlePrevImage();
      } else if (e.key === 'ArrowRight') {
        handleNextImage();
      } else if (e.key === 'Escape') {
        setIsImageModalVisible(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isImageModalVisible, selectedImageIndex, product]);

  // Renderizado de la galería de imágenes
  const renderImageGallery = () => {
    if (!product?.imagenes || product.imagenes.length === 0) {
      return (
        <div className="product-image-container">
          <Empty
            description="No hay imágenes disponibles"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      );
    }

    return (
      <div className="product-gallery">
        <div
          className="main-image-container"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <img
            src={formatImageUrl(product.imagenes[selectedImageIndex]?.ruta_imagen)}
            alt={`${product.name} - Imagen ${selectedImageIndex + 1}`}
            className="main-product-image"
            onClick={() => setIsImageModalVisible(true)}
          />
          {product.imagenes.length > 1 && (
            <div className="image-navigation">
              <span className="image-counter">
                {selectedImageIndex + 1} / {product.imagenes.length}
              </span>
            </div>
          )}
        </div>
        {product.imagenes.length > 1 && (
          <div className="thumbnail-container">
            {product.imagenes.map((imagen, index) => (
              <div
                key={imagen.id}
                className={`thumbnail ${selectedImageIndex === index ? 'active' : ''}`}
                onClick={() => handleImageChange(index)}
              >
                <img
                  src={formatImageUrl(imagen.ruta_imagen)}
                  alt={`${product.name} - Miniatura ${index + 1}`}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Modal de imagen
  const renderImageModal = () => {
    if (!product || !product.imagenes || product.imagenes.length === 0) return null;

    return (
      <Modal
        visible={isImageModalVisible}
        onCancel={() => setIsImageModalVisible(false)}
        footer={null}
        className="image-modal"
        width="90vw"
        centered
      >
        <div className="image-modal-content">
          {product.imagenes.length > 1 && (
            <Button
              icon={<LeftOutlined />}
              className="modal-nav-button left"
              onClick={handlePrevImage}
              disabled={selectedImageIndex === 0}
            />
          )}

          <img
            src={formatImageUrl(product.imagenes[selectedImageIndex]?.ruta_imagen)}
            alt={`${product.name} - Vista completa`}
          />

          {product.imagenes.length > 1 && (
            <>
              <Button
                icon={<RightOutlined />}
                className="modal-nav-button right"
                onClick={handleNextImage}
                disabled={selectedImageIndex === (product.imagenes.length - 1)}
              />

              <div className="modal-image-counter">
                {selectedImageIndex + 1} / {product.imagenes.length}
              </div>
            </>
          )}
        </div>
      </Modal>
    );
  };

  const createConfetti = () => {
    if (isReducedMotion) return; // No mostrar confeti si se prefiere reducción de movimiento

    const confettiCount = isMobile ? 20 : 50; // Reducir la cantidad en móviles
    const newConfetti = [];

    for (let i = 0; i < confettiCount; i++) {
      newConfetti.push({
        id: `confetti-${Date.now()}-${i}`,
        left: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 2}s`,
        size: `${Math.random() * 8 + 5}px`,
      });
    }

    setConfetti(newConfetti);

    setTimeout(() => {
      setConfetti([]);
    }, 3000);
  };

  const handleAddToCart = () => {
    if (!product || product.stock <= 0 || addedToCart) return;

    addToCart(product, quantity);
    setAddedToCart(true);

    createConfetti();

    message.success(`${quantity} ${product.name} agregado al carrito!`, 2);

    setTimeout(() => {
      setAddedToCart(false);
    }, 3000);
  };

  const shareByWhatsApp = () => {
    const productUrl = window.location.href;
    const message = `¡Mira este increíble producto de Bisutería Channels! ${product.name} - ${productUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    if (isMobile) {
      const handleScroll = () => {
        const scrollPosition = window.scrollY;
        setShowMobileControls(scrollPosition > 100);
      };

      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isMobile]);

  // Preparar los datos para las meta tags
  const getProductMetaDescription = () => {
    if (!product) return '';
    let desc = product.description || `Bisutería Channels - ${product.name}`;

    // Agregar información de precio
    if (discountedPrice) {
      desc += ` - Oferta especial: Bs. ${discountedPrice.toFixed(2)} (Normal: Bs. ${product.price.toFixed(2)})`;
    } else {
      desc += ` - Precio: Bs. ${product.price.toFixed(2)}`;
    }

    // Limitar longitud para optimización
    return desc.length > 160 ? `${desc.substring(0, 157)}...` : desc;
  };

  // Construir la URL completa de la imagen
  const getAbsoluteImageUrl = () => {
    if (!product || !product.image) return null;
    const productImage = formatImageUrl(product.image);

    // Si ya es una URL absoluta, devolverla directamente
    if (productImage.startsWith('http')) {
      return productImage;
    }

    // Caso contrario, construir URL absoluta
    return `${window.location.origin}${productImage}`;
  };

  if (loading) {
    return (
      <div className="product-details-loading">
        <Spin size="large" tip="Cargando producto..." />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-not-found">
        <Empty
          description="Producto no encontrado"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
        <Button
          type="primary"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
          className="back-to-shop-button"
        >
          Volver a la tienda
        </Button>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="flutter-style-product-detail" ref={pageTopRef}>
        {/* Meta tags y confetti */}
        {product && (
          <SocialMetaTags
            title={`${product.name} - Bisutería Channels`}
            description={getProductMetaDescription()}
            image={getAbsoluteImageUrl()}
            url={window.location.href}
          />
        )}

        {!isReducedMotion && confetti.map((item) => (
          <div
            key={item.id}
            className="confetti"
            style={{
              left: item.left,
              animationDelay: item.animationDelay,
              width: item.size,
              height: item.size,
            }}
          />
        ))}

        {/* Galería de imágenes móvil mejorada */}
        <div className="mobile-image-gallery" style={{ position: 'relative' }}>
          {/* Precio como etiqueta sobre la imagen, esquina superior derecha, más abajo y con icono */}
          {product?.imagenes && product.imagenes.length > 0 && (
            <div
              className="mobile-price-tag"
              style={{
                position: 'absolute',
                top: 54,
                right: 12,
                zIndex: 20,
                background: 'linear-gradient(135deg, #e91e63, #9c27b0)',
                color: '#fff',
                borderRadius: 14,
                padding: '8px 18px 8px 14px',
                fontWeight: 800,
                fontSize: 18,
                boxShadow: '0 2px 12px #e91e6344',
                letterSpacing: 0.5,
                border: '2px solid #fff',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                minWidth: 110,
                minHeight: 38,
                lineHeight: 1.1,
                position: 'absolute',
              }}
            >
              {/* Icono de etiqueta de precio */}
              <svg width="22" height="22" viewBox="0 0 22 22" style={{ marginRight: 6 }} fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 8.5V5.8C3 4.2536 4.2536 3 5.8 3H8.5C9.05228 3 9.57857 3.21071 9.97631 3.58579L18.4142 12.0237C19.1953 12.8047 19.1953 14.0706 18.4142 14.8517L14.8517 18.4142C14.0706 19.1953 12.8047 19.1953 12.0237 18.4142L3.58579 9.97631C3.21071 9.57857 3 9.05228 3 8.5Z" fill="#fff" fillOpacity="0.25" stroke="#fff" strokeWidth="2" />
                <circle cx="6.5" cy="6.5" r="1.5" fill="#fff" />
              </svg>
              {discountedPrice ? (
                <>
                  <span style={{ textDecoration: 'line-through', opacity: 0.7, fontSize: 14, marginRight: 6 }}>
                    Bs. {product.price.toFixed(2)}
                  </span>
                  <span style={{ fontWeight: 900, fontSize: 18 }}>
                    Bs. {discountedPrice.toFixed(2)}
                  </span>
                  <span style={{ background: '#fff', color: '#e91e63', borderRadius: 8, fontWeight: 700, fontSize: 13, padding: '2px 8px', marginLeft: 6 }}>
                    {product.promotions[0].value}% OFF
                  </span>
                </>
              ) : (
                <span style={{ fontWeight: 900, fontSize: 18 }}>
                  Bs. {product.price.toFixed(2)}
                </span>
              )}
              {/* Triángulo notch de etiqueta */}
              <span style={{
                position: 'absolute',
                bottom: -10,
                right: 0,
                width: 0,
                height: 0,
                borderLeft: '16px solid transparent',
                borderTop: '12px solid #e91e63',
                filter: 'drop-shadow(0 2px 4px #e91e6344)',
                zIndex: 1,
              }} />
            </div>
          )}
          <div className="mobile-main-image-wrapper" style={{ position: 'relative', display: 'flex', flexDirection: 'row', alignItems: 'center', height: '380px' }}>
            {/* Miniaturas verticales superpuestas a la izquierda, solo si hay más de una imagen distinta */}
            {product?.imagenes && product.imagenes.length > 1 && Array.from(new Set(product.imagenes.map(img => img.ruta_imagen))).length > 1 && (
              <div
                className="mobile-thumbnail-vertical-list"
                style={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 10,
                  marginLeft: 4,
                  maxHeight: '320px',
                  overflowY: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  paddingRight: 2,
                }}
              >
                {Array.from(new Set(product.imagenes.map(img => img.ruta_imagen))).map((ruta, index) => {
                  const imagen = product.imagenes.find(img => img.ruta_imagen === ruta);
                  return (
                    <div
                      key={imagen.id}
                      className={`mobile-thumbnail-vertical-item${selectedImageIndex === index ? ' active' : ''}`}
                      onClick={() => handleImageChange(index)}
                      style={{
                        marginBottom: 8,
                        border: selectedImageIndex === index ? '2px solid #e91e63' : '2px solid transparent',
                        borderRadius: 8,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        width: 44,
                        height: 44,
                        background: '#fff',
                        boxShadow: selectedImageIndex === index ? '0 0 8px #e91e6344' : 'none',
                        transition: 'transform 0.18s, box-shadow 0.18s',
                        transform: selectedImageIndex === index ? 'scale(1.15)' : 'scale(1)',
                      }}
                      onMouseDown={e => e.currentTarget.style.transform = 'scale(0.92)'}
                      onMouseUp={e => e.currentTarget.style.transform = selectedImageIndex === index ? 'scale(1.15)' : 'scale(1)'}
                      onMouseLeave={e => e.currentTarget.style.transform = selectedImageIndex === index ? 'scale(1.15)' : 'scale(1)'}
                    >
                      <img
                        src={formatImageUrl(imagen.ruta_imagen)}
                        alt={`${product.name} - Miniatura ${index + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.18s' }}
                      />
                    </div>
                  );
                })}
              </div>
            )}
            {/* Imagen principal */}
            <div className="mobile-main-image-container" style={{ flex: 1, height: '100%' }}>
              {product?.imagenes && product.imagenes.length > 0 ? (
                <img
                  src={formatImageUrl(product.imagenes[selectedImageIndex]?.ruta_imagen)}
                  alt={product.name}
                  className="mobile-main-image"
                  onClick={() => setIsImageModalVisible(true)}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <div className="mobile-no-image">
                  <Empty
                    description="No hay imágenes disponibles"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Badge de promoción */}
        {product.promotions && product.promotions.length > 0 && (
          <motion.div
            className="mobile-promotion-badge"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <Badge.Ribbon
              text={`${product.promotions[0].value}% OFF`}
              color="pink"
            />
          </motion.div>
        )}

        {/* Botón de volver */}
        <Button
          className="mobile-back-button"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          shape="circle"
        />

        {/* Tarjeta de detalles más compacta */}
        <motion.div
          ref={detailCardRef}
          className="product-detail-card"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          style={{ paddingBottom: 90 }}
        >
          <div className="pull-indicator"></div>

          {/* Encabezado con nombre y botón compartir */}
          <div className="product-info-header">
            <div className="product-info-main">
              <Title level={2} className="product-name-desktop">
                {product.name}
              </Title>

              {product.coloresAgrupados?.length > 0 && (
                <div className="product-colors-preview" style={{ marginBottom: 16 }}>
                  {product.coloresAgrupados.map((color, idx) => (
                    <span
                      key={idx}
                      className="color-dot-soft"
                      style={{ backgroundColor: color.toLowerCase() }}
                      title={color}
                    />
                  ))}
                </div>
              )}
              <div className="category-stock-row">
                <span className="category-tag-mobile">
                  {product.categoryName}
                </span>
                <span className={`stock-badge${product.stock > 0 ? '' : ' out'}`}>
                  <span className="stock-dot"></span>
                  {product.stock > 0
                    ? `En stock (${product.stock} disponibles)`
                    : "Agotado"}
                </span>
              </div>
            </div>
            <div className="share-button-container">
              <Button
                className="share-button"
                icon={<ShareAltOutlined />}
                onClick={shareByWhatsApp}
              />
            </div>
          </div>

          {/* Descripción elegante */}
          <motion.div
            className="product-description-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Title level={5}>Descripción</Title>
            <div className={descriptionExpanded ? "expanded-description" : "collapsed-description"}>
              <Text className="product-description">
                {product.description || "Este producto no tiene descripción."}
              </Text>
            </div>
            {product.description?.length > 100 && (
              <div
                className="read-more"
                onClick={() => setDescriptionExpanded(!descriptionExpanded)}
              >
                {descriptionExpanded ? "Mostrar menos" : "Leer más"}
              </div>
            )}
          </motion.div>

          {/* Sucursales (Mobile Modern Style) */}
          {product.sucursalesDisponibles?.length > 0 && (
            <motion.div
              className="modern-branch-availability"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Text className="modern-branch-title">
                Disponibilidad por sucursal
              </Text>
              <div className="modern-branch-list-horizontal">
                {product.sucursalesDisponibles.map(suc => (
                  <div key={suc.sucursal_id} className="modern-branch-item-horizontal">
                    <span className="modern-branch-name">{suc.sucursal_nombre.toUpperCase()}</span>
                    <span className="modern-branch-stock">
                      <span className="stock-number">{suc.stock}</span> unid.
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Elimina el bloque stock-info inferior */}
        </motion.div>

        {/* Contador de cantidad elegante - SIEMPRE VISIBLE */}
        {/* Este bloque ya se movió a la sección de la imagen móvil */}

        {/* BOTÓN FLOTANTE SIEMPRE VISIBLE */}
        <div className="floating-cart-button-container">
          <button
            className={`floating-cart-button ${addedToCart ? 'success' : ''} ${product.stock <= 0 ? 'disabled' : ''}`}
            onClick={handleAddToCart}
            disabled={addedToCart || product.stock <= 0}
          >
            <span className="floating-cart-icon">
              <ShoppingCartOutlined />
            </span>
            {product.stock <= 0
              ? "Agotado"
              : addedToCart
                ? "¡Agregado al carrito!"
                : `Agregar al carrito (${quantity})`}
            {quantity > 1 && !addedToCart && (
              <span className="cart-quantity-badge">{quantity}</span>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="product-details-page" ref={pageTopRef}>
      {/* Meta tags */}
      {product && (
        <SocialMetaTags
          title={`${product.name} - Bisutería Channels`}
          description={getProductMetaDescription()}
          image={getAbsoluteImageUrl()}
          url={window.location.href}
        />
      )}

      <Button
        icon={<ArrowLeftOutlined />}
        className="back-button"
        onClick={() => navigate(-1)}
      >
        Volver
      </Button>

      {/* Botón de volver flotante */}
      <Button
        className="back-button-floating"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
      />

      <div className="product-container">
        <div className="product-grid">
          <div className="product-image-section">
            {renderImageGallery()}
          </div>

          <div className="product-details-section">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="product-info"
            >
              <Title level={1} className="product-name">
                {product.name}
              </Title>

              {product.coloresAgrupados?.length > 0 && (
                <div className="product-colors-preview" style={{ marginBottom: '16px' }}>
                  {product.coloresAgrupados.map((color, idx) => (
                    <span
                      key={idx}
                      className="color-dot-soft"
                      style={{ backgroundColor: color.toLowerCase() }}
                      title={color}
                    />
                  ))}
                </div>
              )}

              <div className="product-price desktop-price">
                {discountedPrice ? (
                  <>
                    <Text delete className="original-price">
                      {product.price.toFixed(2)} Bs.
                    </Text>
                    <Text className="discounted-price">
                      {discountedPrice.toFixed(2)} Bs.
                    </Text>
                  </>
                ) : (
                  <Text className="regular-price">
                    {product.price.toFixed(2)} Bs.
                  </Text>
                )}
              </div>

              <div className="product-description-desktop">
                <Title level={4}>Descripción</Title>
                <Text>
                  {product.description || "No hay descripción disponible para este producto."}
                </Text>
              </div>

              {/* Removed duplicate desktop branches component entirely */}

              {product.promotions && product.promotions.length > 0 && (
                <motion.div
                  className="promotion-details"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Title level={4}>Detalles de la promoción</Title>
                  <Text>
                    <strong>Tipo:</strong> {product.promotions[0].type === 'descuento' ? 'Descuento' : 'Promoción especial'}
                  </Text>
                  <br />
                  <Text>
                    <strong>Descripción:</strong> {product.promotions[0].description}
                  </Text>
                  <br />
                  <Text>
                    <strong>Válido hasta:</strong> {new Date(product.promotions[0].endDate).toLocaleDateString()}
                  </Text>
                </motion.div>
              )}

              {/* Sucursales (Desktop Modern Style) */}
              {product.sucursalesDisponibles?.length > 0 && (
                <motion.div
                  className="modern-branch-availability"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 }}
                >
                  <Text className="modern-branch-title">
                    Disponibilidad por sucursal
                  </Text>
                  <div className="modern-branch-list">
                    {product.sucursalesDisponibles.map(suc => (
                      <div key={suc.sucursal_id} className="modern-branch-item">
                        <span className="modern-branch-name">{suc.sucursal_nombre.toUpperCase()}</span>
                        <span className="modern-branch-stock">
                          <span className="stock-number">{suc.stock}</span> unid.
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              <div className="add-to-cart-section">
                <div className="quantity-selector">
                  <Text>Cantidad:</Text>
                  <div className="quantity-input-group">
                    <Button
                      onClick={handleDecreaseQuantity}
                      disabled={quantity <= 1}
                    >
                      -
                    </Button>
                    <div className="quantity-display">
                      {quantity}
                    </div>
                    <Button
                      onClick={handleIncreaseQuantity}
                      disabled={quantity >= product.stock}
                    >
                      +
                    </Button>
                  </div>
                </div>

                <motion.div
                  className="action-buttons"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <Button
                    type="primary"
                    size="large"
                    icon={<ShoppingCartOutlined style={{ fontSize: '20px' }} />}
                    onClick={handleAddToCart}
                    disabled={product.stock <= 0 || addedToCart}
                    className="add-to-cart-button"
                  >
                    {addedToCart ? '¡Agregado al carrito!' : 'Agregar al carrito'}
                  </Button>

                  <Button
                    type="default"
                    size="large"
                    icon={<ShareAltOutlined />}
                    className="share-button"
                    onClick={shareByWhatsApp}
                  >
                    Compartir por WhatsApp
                  </Button>
                </motion.div>
              </div>

              <div className="product-category">
                <Text type="secondary">Categoría: </Text>
                <span className="category-tag">{product.categoryName}</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {renderImageModal()}
    </div>
  );
};

export default ProductDetailsPage;
